// Outcome card shown below an action form. Renders one of:
//   - "Pending" (sky chip while a wallet is mid-sign)
//   - "Errored" (red banner)
//   - "Tx" (green banner with hash + explorer link)
//   - "View" (slate card with the returned JSON value pretty-printed)
//   - "NeedsBridgeCli" (amber banner for actions that require off-chain
//     proof generation; backend returned NEEDS_EXTERNAL_PROOF).

export type ActionOutcome =
  | { kind: "pending"; message: string }
  | { kind: "errored"; message: string }
  | { kind: "tx"; hash: string; explorerUrl?: string; extra?: TxRow[] }
  | { kind: "view"; value: unknown }
  | { kind: "needs_bridge_cli"; message: string };

/** Additional rows shown under a primary tx outcome — e.g. NEAR batch
 *  signing returns multiple hashes; the result card lists them. */
export interface TxRow {
  hash: string;
  explorerUrl?: string;
  label?: string;
}

export function ResultCard({ outcome }: { outcome: ActionOutcome }) {
  return <div className="mt-4">{render(outcome)}</div>;
}

function TxLine({ hash, explorerUrl, label }: { hash: string; explorerUrl?: string; label?: string }) {
  if (!hash) return null;
  return (
    <div className="mt-1 font-mono text-xs">
      {label && <span className="mr-2 text-emerald-700 dark:text-emerald-400">{label}</span>}
      {explorerUrl ? (
        <a
          href={explorerUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-near-green-dark dark:text-near-green underline hover:text-near-green-dark"
        >
          {hash}
        </a>
      ) : (
        <span className="text-emerald-700 dark:text-emerald-300">{hash}</span>
      )}
    </div>
  );
}

function render(outcome: ActionOutcome): React.ReactElement {
  switch (outcome.kind) {
    case "pending":
      return (
        <div className="rounded-md border border-sky-800/60 bg-sky-900/30 px-3 py-2 text-sm text-sky-200" data-testid="outcome-pending">
          {outcome.message}
        </div>
      );
    case "errored":
      return (
        <div className="rounded-md border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/40 px-3 py-2 text-sm text-red-700 dark:text-red-200" data-testid="outcome-errored">
          {outcome.message}
        </div>
      );
    case "tx":
      return (
        <div className="rounded-md border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-200" data-testid="outcome-tx">
          <div className="font-medium">
            {outcome.extra && outcome.extra.length > 0
              ? `Transactions signed and submitted (${outcome.extra.length + 1})`
              : "Transaction signed and submitted"}
          </div>
          <TxLine hash={outcome.hash} explorerUrl={outcome.explorerUrl} />
          {outcome.extra?.map((row, i) => (
            <TxLine key={row.hash || i} hash={row.hash} explorerUrl={row.explorerUrl} label={row.label} />
          ))}
        </div>
      );
    case "view":
      return (
        <div className="rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-800 dark:text-slate-200" data-testid="outcome-view">
          <div className="font-medium">Result</div>
          <pre className="mt-1 overflow-auto rounded bg-slate-100 dark:bg-slate-950 px-2 py-1 font-mono text-xs text-slate-700 dark:text-slate-300">
            {JSON.stringify(outcome.value, null, 2)}
          </pre>
        </div>
      );
    case "needs_bridge_cli":
      return (
        <div className="rounded-md border border-amber-300 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-900 dark:text-amber-100 space-y-2" data-testid="outcome-bridge-cli">
          <div className="font-medium">
            Needs an off-chain proof — produce it with bridge-cli, then sign with your wallet
          </div>
          <pre className="overflow-x-auto whitespace-pre-wrap rounded bg-slate-200/60 dark:bg-slate-950/60 px-3 py-2 font-mono text-xs text-amber-800 dark:text-amber-200">
            {outcome.message}
          </pre>
        </div>
      );
  }
}
